<template>
  <div class="enterprise-rag">
    <section class="hero-sub">
      <div class="container">
        <router-link to="/" class="back-link">← {{ $t('auditForm.backToHome') }}</router-link>
        <div class="badge">{{ $t('enterpriseRAG.preSale.badge') }}</div>
        <h1>{{ $t('enterpriseRAG.title') }}</h1>
        <p class="subtitle">{{ $t('enterpriseRAG.subtitle') }}</p>
      </div>
    </section>

    <section class="content-outline">
      <div class="container">
        <h2>{{ $t('enterpriseRAG.outline.title') }}</h2>
        <div class="outline-grid">
          <div v-for="(item, index) in $tm('enterpriseRAG.outline.items')" :key="index" class="outline-card">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="sneak-peak">
      <div class="container">
        <h2>{{ $t('enterpriseRAG.sneakPeak.title') }}</h2>
        <div class="sneak-peak-grid">
          <div v-for="(box, index) in $tm('enterpriseRAG.sneakPeak.boxes')" :key="index" class="sneak-peak-box">
            <div class="box-icon">{{ box.icon }}</div>
            <h3>{{ box.title }}</h3>
            <p>{{ box.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="pre-sale">
      <div class="container">
        <div class="pre-sale-box">
          <h2>{{ $t('enterpriseRAG.preSale.cta') }}</h2>
          <p class="discount-notice">{{ $t('enterpriseRAG.preSale.discount') }}</p>
          
          <form v-if="!submitted" @submit.prevent="handleSubmit" class="waitlist-form">
            <div class="form-group">
              <label for="email">{{ $t('enterpriseRAG.preSale.form.email') }}</label>
              <input 
                type="email" 
                id="email" 
                v-model="email" 
                required 
                placeholder="you@company.com"
              >
            </div>
            <button type="submit" :disabled="loading" class="cta-button">
              {{ loading ? $t('auditForm.sending') : $t('enterpriseRAG.preSale.form.submit') }}
            </button>
          </form>
          
          <div v-else class="success-message">
            <p>{{ $t('enterpriseRAG.preSale.form.success') }}</p>
          </div>
          
          <p class="storage-note">
            <small>By signing up, you agree to receive updates about the Enterprise RAG Handbook. We respect your privacy.</small>
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { db } from '@/firebase'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

const email = ref('')
const loading = ref(false)
const submitted = ref(false)

const handleSubmit = async () => {
  if (!email.value) return
  loading.value = true
  
  try {
    await addDoc(collection(db, 'waitlist'), {
      email: email.value,
      source: 'enterprise-rag-book',
      timestamp: serverTimestamp()
    })
    submitted.value = true
  } catch (error) {
    console.error('Signup error:', error)
    alert('Something went wrong. Please try again.')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.enterprise-rag {
  padding-bottom: 80px;
}

.hero-sub {
  background-color: var(--primary-light);
  padding: 60px 0;
  text-align: center;
}

.back-link {
  display: inline-block;
  margin-bottom: 30px;
  text-decoration: none;
  color: var(--primary-color);
  font-weight: 600;
}

.badge {
  display: block;
  width: fit-content;
  margin: 0 auto 20px;
  background-color: var(--accent-color);
  color: var(--text-color);
  padding: 5px 15px;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.9rem;
}

h1 {
  font-size: 2.5rem;
  margin-bottom: 20px;
  color: var(--text-color);
}

.subtitle {
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto;
  color: #555;
}

.content-outline {
  padding: 80px 0;
}

h2 {
  text-align: center;
  margin-bottom: 40px;
  font-size: 2rem;
}

.outline-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.outline-card {
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  border-top: 4px solid var(--primary-color);
  transition: transform 0.3s;
}

.outline-card:hover {
  transform: translateY(-5px);
}

.outline-card h3 {
  margin-bottom: 15px;
  color: var(--primary-color);
}

.sneak-peak {
  padding: 80px 0;
  background-color: var(--gray-light);
}

.sneak-peak-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.sneak-peak-box {
  background: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  transition: transform 0.3s, box-shadow 0.3s;
}

.sneak-peak-box:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.box-icon {
  font-size: 2.5rem;
  margin-bottom: 20px;
}

.sneak-peak-box h3 {
  margin-bottom: 15px;
  font-size: 1.25rem;
}

.pre-sale {
  padding: 60px 0;
}

.pre-sale-box {
  max-width: 600px;
  margin: 0 auto;
  background-color: var(--gray-light);
  padding: 40px;
  border-radius: 12px;
  text-align: center;
}

.discount-notice {
  font-weight: bold;
  color: var(--primary-color);
  font-size: 1.1rem;
  margin-bottom: 30px;
}

.waitlist-form {
  text-align: left;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
}

.form-group input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1rem;
}

.cta-button {
  width: 100%;
  padding: 15px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
}

.cta-button:hover {
  background-color: var(--primary-dark);
}

.cta-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.success-message {
  padding: 20px;
  background-color: #d4edda;
  color: #155724;
  border-radius: 6px;
  font-weight: bold;
}

.storage-note {
  margin-top: 20px;
  color: #777;
}

@media (max-width: 768px) {
  h1 {
    font-size: 2rem;
  }
}
</style>
